import React, { useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { isoDate } from "@/lib/date";
import { useNavPrefs, applyNavPrefs } from "@/lib/navPrefs";
import { ALL_NAV_LINKS } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Building2 } from "lucide-react";
import { ArrowUp, ArrowDown, Settings, RotateCcw, Eye, EyeOff, Download, ShieldCheck, FileJson, FileSpreadsheet, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

// Helper to download a JSON file (xlsx uses XLSX.writeFile directly).
function downloadFile(filename, content, mime) {
  const blob = new Blob([content], { type: mime + ";charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export default function Parametres() {
  const { user } = useAuth();
  const role = user?.role || "commercial";
  const userId = user?.id || user?.email || "anon";
  const isAdmin = role === "admin";
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Company settings
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);

  React.useEffect(() => {
    if (!isAdmin) return;
    api.get("/settings/company").then(({ data }) => setCompany(data)).catch(() => setCompany({}));
  }, [isAdmin]);

  const saveCompany = async () => {
    setCompanyLoading(true);
    try {
      await api.put("/settings/company", company);
      toast.success("Société enregistrée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setCompanyLoading(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) { toast.error("Logo trop lourd (max 500 KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCompany((c) => ({ ...c, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  // Only nav links available for this user's role
  const baseLinks = useMemo(
    () => ALL_NAV_LINKS.filter((l) => l.roles.includes(role)),
    [role],
  );

  const { prefs, update, reset } = useNavPrefs(userId, baseLinks);
  const applied = applyNavPrefs(baseLinks, prefs);
  const ordered = applied.ordered || baseLinks;
  const hiddenSet = new Set((prefs && prefs.hidden) || []);

  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const list = [...ordered];
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    update({ order: list.map((l) => l.to) });
  };

  const toggleHidden = (path) => {
    const next = new Set(hiddenSet);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    update({ hidden: Array.from(next) });
  };

  const resetAll = () => {
    reset();
    toast.success("Préférences réinitialisées");
  };

  const downloadBackup = async (format) => {
    setBackupLoading(true);
    try {
      const { data } = await api.get("/backup/full");
      const today = isoDate().replace(/-/g, "");
      if (format === "json") {
        downloadFile(
          `garage-rm-backup-${today}.json`,
          JSON.stringify(data, null, 2),
          "application/json",
        );
        toast.success("Sauvegarde JSON téléchargée");
        return;
      }
      // XLSX: one sheet per collection
      const c = data.collections || {};
      const wb = XLSX.utils.book_new();
      const order = ["clients", "ventes", "paiements", "interventions", "users"];
      const sizes = {};
      order.forEach((name) => {
        const rows = c[name] || [];
        sizes[name] = rows.length;
        if (rows.length === 0) {
          const ws = XLSX.utils.aoa_to_sheet([["(aucune donnée)"]]);
          XLSX.utils.book_append_sheet(wb, ws, name);
          return;
        }
        const headers = Array.from(
          rows.reduce((set, r) => { Object.keys(r).forEach((k) => set.add(k)); return set; }, new Set()),
        );
        const aoa = [headers, ...rows.map((r) => headers.map((h) => r[h] ?? ""))];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 14) }));
        XLSX.utils.book_append_sheet(wb, ws, name);
      });
      XLSX.writeFile(wb, `garage-rm-backup-${today}.xlsx`);
      toast.success(`Sauvegarde Excel téléchargée (${sizes.clients} clients · ${sizes.ventes} ventes · ${sizes.paiements} paiements · ${sizes.interventions} interventions)`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la sauvegarde");
    } finally {
      setBackupLoading(false);
    }
  };

  const visibleCount = ordered.filter((l) => !hiddenSet.has(l.to)).length;

  const handleRestoreFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = ""; // allow re-selecting the same file later

    // First confirmation: explicit action
    const firstOk = window.confirm(
      `⚠️ RESTAURATION DEPUIS UN BACKUP\n\nFichier : ${file.name}\n\nCette action va :\n• ÉCRASER tous les clients, ventes, paiements et interventions actuels\n• Conserver les utilisateurs/mots de passe actuels\n• Sauvegarder automatiquement l'état actuel (annulation possible côté admin)\n\nVoulez-vous continuer ?`,
    );
    if (!firstOk) return;

    // Read & parse JSON
    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      toast.error("Fichier JSON invalide");
      return;
    }
    if (!parsed.collections) {
      toast.error("Format inconnu — ce n'est pas une sauvegarde Gestion Concession Automobile");
      return;
    }

    // Second confirmation: type the magic word
    const typed = window.prompt(
      `Pour confirmer définitivement, tapez : RESTAURER\n\n(Toute saisie différente annulera l'opération.)`,
    );
    if (typed !== "RESTAURER") {
      toast.error("Confirmation annulée");
      return;
    }

    setRestoreLoading(true);
    try {
      const { data } = await api.post("/backup/restore", {
        collections: parsed.collections,
        confirm: "RESTAURER",
      });
      const s = data.restored || {};
      toast.success(`Restauration réussie · ${s.clients || 0} clients · ${s.ventes || 0} ventes · ${s.paiements || 0} paiements · ${s.interventions || 0} interventions`, { duration: 8000 });
      // Force reload to refetch everything from the new DB
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la restauration");
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="parametres-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold flex items-center gap-2">
            <Settings size={12} /> Personnalisation
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">
            Paramètres
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Réorganisez et masquez les onglets du menu selon votre flux de travail.
            Vos préférences sont sauvegardées localement sur cet appareil.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resetAll}
          data-testid="parametres-reset"
          className="rounded-sm"
        >
          <RotateCcw size={14} className="mr-1" /> Réinitialiser
        </Button>
      </header>

      {/* Self-service: change my own password (all roles) */}
      <MonMotDePasse />

      <Card className="rounded-sm border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold">Menu de navigation</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {visibleCount} onglet{visibleCount > 1 ? "s" : ""} visible{visibleCount > 1 ? "s" : ""} sur {ordered.length}
            </div>
          </div>
        </div>

        <ul className="space-y-2" data-testid="parametres-nav-list">
          {ordered.map((l, idx) => {
            const Icon = l.icon;
            const hidden = hiddenSet.has(l.to);
            return (
              <li
                key={l.to}
                data-testid={`parametres-item-${l.testId}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-colors ${
                  hidden ? "bg-slate-50 opacity-60" : "bg-white"
                } border-border`}
              >
                {/* Reorder controls */}
                <div className="flex flex-col">
                  <button
                    onClick={() => move(idx, idx - 1)}
                    disabled={idx === 0}
                    aria-label="Monter"
                    data-testid={`parametres-up-${l.testId}`}
                    className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => move(idx, idx + 1)}
                    disabled={idx === ordered.length - 1}
                    aria-label="Descendre"
                    data-testid={`parametres-down-${l.testId}`}
                    className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>

                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <Icon size={16} className="text-klein flex-shrink-0" />
                  <span className={`text-sm font-medium truncate ${hidden ? "line-through text-muted-foreground" : ""}`}>
                    {l.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-auto">
                    {l.to}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {hidden ? <EyeOff size={14} className="text-muted-foreground" /> : <Eye size={14} className="text-klein" />}
                  <Switch
                    checked={!hidden}
                    onCheckedChange={() => toggleHidden(l.to)}
                    data-testid={`parametres-toggle-${l.testId}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>

        <p className="text-[11px] text-muted-foreground mt-4">
          Astuce : utilisez les flèches pour réordonner, l'interrupteur pour masquer un onglet.
          Les nouvelles fonctionnalités ajoutées au menu apparaîtront automatiquement à la fin.
        </p>
      </Card>

      {/* Company settings - admin only */}
      {isAdmin && company && (
        <Card className="rounded-sm border-border p-4 border-l-4 border-l-klein" data-testid="parametres-company-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-klein font-bold">
            <Building2 size={12} /> Identité société
          </div>
          <h3 className="font-display font-bold text-lg mt-1">Informations affichées sur les reçus PDF</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl mb-4">
            Ces informations apparaissent en en-tête et pied de page de chaque reçu de paiement généré.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Nom du garage *</Label>
              <Input
                data-testid="company-nom"
                value={company.nom || ""}
                onChange={(e) => setCompany({ ...company, nom: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Téléphone</Label>
              <Input
                data-testid="company-tel"
                value={company.telephone || ""}
                onChange={(e) => setCompany({ ...company, telephone: e.target.value })}
                placeholder="01 23 45 67 89"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                data-testid="company-email"
                type="email"
                value={company.email || ""}
                onChange={(e) => setCompany({ ...company, email: e.target.value })}
                placeholder="contact@garage.fr"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Site web</Label>
              <Input
                data-testid="company-web"
                value={company.site_web || ""}
                onChange={(e) => setCompany({ ...company, site_web: e.target.value })}
                placeholder="www.garage.fr"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">SIRET / N° TVA Intracom</Label>
              <Input
                data-testid="company-mf"
                value={company.matricule_fiscal || ""}
                onChange={(e) => setCompany({ ...company, matricule_fiscal: e.target.value })}
                placeholder="123 456 789 00012"
                className="mt-1 rounded-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Adresse</Label>
              <Textarea
                data-testid="company-adresse"
                value={company.adresse || ""}
                onChange={(e) => setCompany({ ...company, adresse: e.target.value })}
                rows={2}
                placeholder="12 rue de la Paix, 75002 Paris"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">RIB / IBAN bancaire</Label>
              <Input
                data-testid="company-rib"
                value={company.rib || ""}
                onChange={(e) => setCompany({ ...company, rib: e.target.value })}
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                className="mt-1 rounded-sm"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider">Logo (PNG / JPG, max 500 KB)</Label>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {company.logo_data_url && (
                  <img
                    src={company.logo_data_url}
                    alt="Logo"
                    className="h-16 w-16 object-contain border border-border rounded-sm bg-white"
                    data-testid="company-logo-preview"
                  />
                )}
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer hover:bg-slate-50 text-sm">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={onLogoFile}
                    data-testid="company-logo-input"
                  />
                  Choisir une image
                </label>
                {company.logo_data_url && (
                  <button
                    type="button"
                    onClick={() => setCompany({ ...company, logo_data_url: "" })}
                    data-testid="company-logo-remove"
                    className="text-xs text-destructive hover:underline"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              onClick={saveCompany}
              disabled={companyLoading}
              data-testid="company-save"
              className="bg-klein hover:bg-blue-800 text-white rounded-sm"
            >
              {companyLoading ? "Enregistrement…" : "Enregistrer la société"}
            </Button>
          </div>
        </Card>
      )}

      {/* Backup section - admin only */}
      {isAdmin && (
        <Card className="rounded-sm border-border p-4 border-l-4 border-l-klein" data-testid="parametres-backup-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-klein font-bold">
                <ShieldCheck size={12} /> Sécurité · Sauvegarde · Migration
              </div>
              <h3 className="font-display font-bold text-lg mt-1">Sauvegarde & restauration des données</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                Téléchargez une copie complète de votre base sur votre PC : clients, ventes,
                paiements, interventions et utilisateurs (mots de passe exclus).
                <span className="block mt-1 text-klein font-semibold">
                  ⭐ Indispensable avant un changement de compte / serveur — le fichier JSON peut être réimporté plus bas.
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Button
              type="button"
              disabled={backupLoading}
              onClick={() => downloadBackup("json")}
              data-testid="parametres-backup-json"
              className="bg-klein hover:bg-blue-800 text-white rounded-sm justify-start h-auto py-3"
            >
              <FileJson size={18} className="mr-2 flex-shrink-0" />
              <div className="text-left">
                <div className="text-sm font-semibold">Sauvegarde JSON</div>
                <div className="text-[11px] opacity-80 font-normal">Format complet · ré-importable pour migration de compte</div>
              </div>
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={backupLoading}
              onClick={() => downloadBackup("xlsx")}
              data-testid="parametres-backup-csv"
              className="rounded-sm justify-start h-auto py-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <FileSpreadsheet size={18} className="mr-2 flex-shrink-0" />
              <div className="text-left">
                <div className="text-sm font-semibold">Sauvegarde Excel</div>
                <div className="text-[11px] opacity-80 font-normal">Format .xlsx · lecture humaine, non ré-importable</div>
              </div>
            </Button>
          </div>

          {backupLoading && (
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
              <Download size={12} className="animate-pulse" /> Préparation de la sauvegarde…
            </p>
          )}

          {/* Restore section */}
          <div className="mt-6 pt-4 border-t border-dashed border-red-200">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-display font-bold text-sm text-red-700">Restauration depuis JSON</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Importez un fichier JSON pour <strong>écraser la base actuelle</strong>.
                  Utile lors d'un changement de compte ou de serveur.
                  Les utilisateurs/mots de passe ne sont pas touchés. Un instantané automatique
                  est sauvegardé avant la restauration.
                </p>
              </div>
            </div>
            <label
              data-testid="parametres-restore-input"
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-semibold cursor-pointer transition-colors border ${
                restoreLoading
                  ? "bg-slate-200 text-slate-500 border-slate-200 cursor-not-allowed"
                  : "bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
              }`}
            >
              <Upload size={14} />
              {restoreLoading ? "Restauration en cours…" : "Choisir un fichier JSON à restaurer"}
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                disabled={restoreLoading}
                onChange={handleRestoreFile}
              />
            </label>
          </div>

          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            💡 Recommandation : effectuez une sauvegarde manuelle <strong>au moins une fois par semaine</strong>
            et conservez plusieurs versions (cloud personnel, clé USB, disque externe). Avant tout changement
            de compte ou serveur : exportez JSON, puis importez-le sur le nouveau compte.
          </p>
        </Card>
      )}
    </div>
  );
}

// ----- Self-service password change (any role) -----
function MonMotDePasse() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (next !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.put("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe mis à jour");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-sm border-border p-4" data-testid="mon-mot-de-passe-card">
      <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
        Sécurité
      </div>
      <h3 className="font-display font-bold text-lg mt-1">Mon mot de passe</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-xl">
        Modifiez votre propre mot de passe. Choisissez un mot de passe difficile à deviner.
      </p>

      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 mt-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Mot de passe actuel</Label>
          <Input
            data-testid="self-pw-current"
            type="password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="mt-1 rounded-sm"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Nouveau mot de passe</Label>
          <Input
            data-testid="self-pw-new"
            type="password"
            minLength={6}
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="mt-1 rounded-sm"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Confirmation</Label>
          <Input
            data-testid="self-pw-confirm"
            type="password"
            minLength={6}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1 rounded-sm"
          />
        </div>
        <div className="sm:col-span-3 flex justify-end">
          <Button
            type="submit"
            data-testid="self-pw-submit"
            disabled={loading}
            className="bg-klein hover:bg-blue-800 text-white rounded-sm"
          >
            {loading ? "Mise à jour…" : "Mettre à jour"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
